import React from "react";
import { useLang } from "@/context/LanguageContext";
import NumberStepper from "@/components/NumberStepper";
import { Info } from "lucide-react";

export const DEFAULT_PAX_CONFIG = {
  sections: 2, frame_width: 50, frame_height: 236,
  hinged_doors: 0, mirror_doors: 0, sliding_doors: 0, handles: 0,
  drawers: 0, baskets: 0, trays: 0, trouser_hangers: 0, multi_hangers: 0, other_pullouts: 0,
  shelves: 0, rails: 0, shoe_shelf: 0, frame_dividers: 0, corner_sections: 0,
  lighting: 0, other_unknown: 0, packaging_removal: false,
};

// Editable PAX inputs. Used both for the manual calculator AND the
// AI-recognition confirmation screen (same fields, same pricing engine).
export default function PaxFields({ config, onChange }) {
  const { t } = useLang();
  const upd = (k, v) => onChange({ ...config, [k]: v });

  const interiorFields = [
    ["drawers", t.pax.drawers], ["baskets", t.pax.baskets], ["trays", t.pax.trays],
    ["trouser_hangers", t.pax.trouserHangers], ["multi_hangers", t.pax.multiHangers],
    ["other_pullouts", t.pax.otherPullouts], ["shelves", t.pax.shelves], ["rails", t.pax.rails],
    ["shoe_shelf", t.pax.shoeShelf], ["frame_dividers", t.pax.frameDividers], ["corner_sections", t.pax.cornerSections],
  ];

  return (
    <div data-testid="pax-fields">
      <NumberStepper testId="pax-sections" label={t.pax.sections} value={config.sections} onChange={(v) => upd("sections", v)} min={1} max={20} />

      {/* width — informational only */}
      <div className="py-3">
        <div className="flex items-center gap-1.5 text-sm font-medium text-stone-800 mb-2">{t.pax.frameWidth}
          <span className="text-[11px] font-normal text-stone-400 inline-flex items-center gap-1"><Info className="w-3 h-3" />{t.pax.infoOnly}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[50, 75, 100].map((w) => (
            <button key={w} type="button" onClick={() => upd("frame_width", w)}
              className={`py-2.5 rounded-xl border text-sm font-medium ${config.frame_width === w ? "border-[#1E3A2F] bg-[#1E3A2F] text-white" : "border-stone-300 text-stone-700 hover:bg-stone-50"}`}
              data-testid={`pax-width-${w}`}>{w} {t.pax.cm}</button>
          ))}
        </div>
      </div>

      {/* height — informational only */}
      <div className="py-3 border-b border-stone-100">
        <div className="flex items-center gap-1.5 text-sm font-medium text-stone-800 mb-2">{t.pax.frameHeight}
          <span className="text-[11px] font-normal text-stone-400 inline-flex items-center gap-1"><Info className="w-3 h-3" />{t.pax.infoOnly}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[201, 236].map((h) => (
            <button key={h} type="button" onClick={() => upd("frame_height", h)}
              className={`py-2.5 rounded-xl border text-sm font-medium ${config.frame_height === h ? "border-[#1E3A2F] bg-[#1E3A2F] text-white" : "border-stone-300 text-stone-700 hover:bg-stone-50"}`}
              data-testid={`pax-height-${h}`}>{h} {t.pax.cm}</button>
          ))}
        </div>
      </div>

      <div className="text-xs uppercase tracking-widest text-stone-400 font-semibold pt-4 pb-1">{t.pax.doorsTitle}</div>
      <div className="divide-y divide-stone-100">
        <NumberStepper testId="pax-hinged" label={t.pax.hingedDoors} value={config.hinged_doors} onChange={(v) => upd("hinged_doors", v)} max={40} />
        <NumberStepper testId="pax-mirror-doors" label={t.pax.mirrorDoors} value={config.mirror_doors} onChange={(v) => upd("mirror_doors", v)} max={40} />
        <NumberStepper testId="pax-handles" label={t.pax.handles} value={config.handles} onChange={(v) => upd("handles", v)} max={80} />
        <NumberStepper testId="pax-sliding" label={t.pax.slidingDoors} value={config.sliding_doors} onChange={(v) => upd("sliding_doors", v)} max={20} />
      </div>

      <div className="text-xs uppercase tracking-widest text-stone-400 font-semibold pt-4 pb-1">{t.pax.interiorTitle}</div>
      <div className="divide-y divide-stone-100">
        {interiorFields.map(([k, label]) => (
          <NumberStepper key={k} testId={`pax-${k}`} label={label} value={config[k]} onChange={(v) => upd(k, v)} max={80} />
        ))}
        <NumberStepper testId="pax-lighting" label={t.pax.lighting} value={config.lighting} onChange={(v) => upd("lighting", v)} max={20} />
        <NumberStepper testId="pax-other_unknown" label={t.pax.otherUnknown} value={config.other_unknown} onChange={(v) => upd("other_unknown", v)} max={40} />
      </div>

      <div className="text-xs uppercase tracking-widest text-stone-400 font-semibold pt-4 pb-1">{t.pax.packagingTitle}</div>
      <label className="flex items-start gap-3 rounded-xl border border-stone-200 p-3 cursor-pointer" data-testid="pax-packaging-label">
        <input type="checkbox" checked={!!config.packaging_removal} onChange={(e) => upd("packaging_removal", e.target.checked)} className="mt-1 w-4 h-4 accent-[#1E3A2F]" data-testid="pax-packaging-removal" />
        <span><span className="block text-sm font-medium text-stone-800">{t.pax.packagingRemoval}</span><span className="block text-xs text-stone-500">{t.pax.packagingKeep}</span></span>
      </label>

      <p className="mt-4 flex items-start gap-2 text-xs text-stone-500 bg-stone-50 rounded-lg p-3">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-stone-400" /> {t.pax.includedNote}
      </p>
    </div>
  );
}
